import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePromiseTracker } from 'react-promise-tracker';
import { XGrid } from '@AMIEWEB/Common';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { selectOrderPayPackages } from '@AMIEWEB/Order/Store/Order.selectors';
import { selectFilters } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { transformOrderPayPackages } from '@AMIEWEB/Order/Store/helpers/sagaHelper';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import {
  facilityOrderPayPackageColumns,
  facilityOrderPayPackagesColumnVisibility,
} from './FacilityOrderPayPackagesUtils';
import { filterFacilityOrderPayPackagesRows } from './helpers';

export const FacilityOrderPayPackagesGrid = ({ facilityId }: { facilityId: number }) => {
  const gridTag = `facility-order-pay-packages-grid-${facilityId}`;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { promiseInProgress: getOrderPayPackagesInProgress } = usePromiseTracker({ area: 'get-Order-Pay-Packages' });
  const orderPayPackages = useSelector(selectOrderPayPackages);
  const filterObject = useSelector(selectFilters);
  const userPreference = useSelector(selectUserPreference);

  const [columns] = useState(() => facilityOrderPayPackageColumns(t));

  const sourceRows = useMemo(
    () => (orderPayPackages ? transformOrderPayPackages(orderPayPackages) : []),
    [orderPayPackages],
  );

  const rows = useMemo(
    () => filterFacilityOrderPayPackagesRows(sourceRows || [], filterObject),
    [sourceRows, filterObject],
  );

  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(gridTag));
    }
  }, [userPreference?.loaded, gridTag, dispatch]);

  const fetchOrderPayPackages = () => {
    dispatch(orderDataActions.getOrderPayPackages({ facilityId }));
  };

  return (
    <LayoutGrid>
      <LayoutGridItem xs={12}>
        <XGrid
          getRowId={row => row.scenarioId}
          gridTag={gridTag}
          columns={columns}
          rows={rows}
          autoHeight
          checkboxSelection={false}
          disableSelectionOnClick
          loading={getOrderPayPackagesInProgress}
          defaultColumnVisibilityModel={facilityOrderPayPackagesColumnVisibility}
          toolbarProps={{
            refreshButton: true,
            refreshGrid: () => fetchOrderPayPackages(),
          }}
        />
      </LayoutGridItem>
    </LayoutGrid>
  );
};
